import React, {Component} from 'react';

// Regex que matchea un identifier válido de métrica (built-in o custom).
// Mismo criterio que el parser del backend: letras minúsculas + dígitos + underscore,
// debe comenzar con letra.
const IDENTIFIER_REGEX = /[a-z_][a-z0-9_]*/gi;


/**
 * Input contenteditable que muestra una fórmula como mezcla de:
 *  - Chips (pills) por cada métrica referenciada — DOM `<span class="formula-chip" data-identifier="..." contenteditable="false">DisplayName</span>`.
 *  - Texto plano para operadores, constantes y paréntesis.
 *
 * Estrategia ownership: el DOM es source of truth después del mount. El parent
 * pasa `initialValue` para hidratar 1 vez y recibe `onChange(formulaString)` con
 * los identifiers serializados (lo que el backend espera). Esto evita flicker
 * de re-renderizar React por cada keystroke.
 *
 * Métodos públicos vía ref:
 *  - insertToken({type, identifier?, displayName?, text?}) — inserta en el cursor.
 *  - getFormula() — serializa DOM → string con identifiers.
 *  - setFormula(string) — re-renderiza el DOM desde un string. Útil si el parent
 *    necesita resetear (ej. abrir el modal en modo edición con otra métrica).
 */
export class ChipFormulaInput extends Component {

    constructor(props) {
        super(props);
        this._ref = React.createRef();
        this.handleInput = this.handleInput.bind(this);
        this.handlePaste = this.handlePaste.bind(this);
    }

    componentDidMount() {
        this._renderFormula(this.props.initialValue || '');
    }

    // Públicos (via ref desde el parent).
    insertToken(token) {
        this._focus();
        if (token.type === 'metric') {
            const chip = this._buildChipNode(token.identifier, token.displayName, !!token.isBroken);
            this._insertNodeAtCursor(chip);
            // Sumamos un espacio + zero-width space después del chip para no quedar "atrapados" dentro.
            this._insertNodeAtCursor(document.createTextNode('\u00A0'));
        } else if (token.type === 'operator') {
            this._insertNodeAtCursor(document.createTextNode(token.text));
        }
        this._emitChange();
    }

    getFormula() {
        return this._serializeDom();
    }

    setFormula(formulaString) {
        this._renderFormula(formulaString || '');
        this._emitChange();
    }

    // --- Internos ---

    _focus() {
        const el = this._ref.current;
        if (!el) return;
        // Si no hay selección dentro del input, coloca el cursor al final.
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0 || !el.contains(sel.anchorNode)) {
            el.focus();
            const range = document.createRange();
            range.selectNodeContents(el);
            range.collapse(false);
            sel.removeAllRanges();
            sel.addRange(range);
        } else {
            el.focus();
        }
    }

    _insertNodeAtCursor(node) {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        const range = sel.getRangeAt(0);
        range.deleteContents();
        range.insertNode(node);
        // Mueve cursor justo después del nodo insertado.
        range.setStartAfter(node);
        range.setEndAfter(node);
        sel.removeAllRanges();
        sel.addRange(range);
    }

    _buildChipNode(identifier, displayName, isBroken) {
        const span = document.createElement('span');
        span.className = 'formula-chip' + (isBroken ? ' -broken' : '');
        span.setAttribute('data-identifier', identifier);
        span.setAttribute('contenteditable', 'false');
        if (isBroken) span.setAttribute('title', 'Referencia rota');
        span.textContent = displayName || identifier;
        return span;
    }

    _renderFormula(formulaString) {
        const el = this._ref.current;
        if (!el) return;
        el.innerHTML = '';
        const {catalogById} = this.props;
        const tokens = this._tokenize(formulaString);
        tokens.forEach((tok) => {
            if (tok.type === 'identifier') {
                const display = (catalogById && catalogById[tok.value] && catalogById[tok.value].displayName) || tok.value;
                const isBroken = !(catalogById && catalogById[tok.value]);
                el.appendChild(this._buildChipNode(tok.value, display, isBroken));
            } else {
                el.appendChild(document.createTextNode(tok.value));
            }
        });
    }

    _tokenize(str) {
        // Devuelve [{type:'identifier'|'literal', value}]. Identifiers se separan,
        // todo lo demás (operadores, números, paréntesis, espacios) va como literal.
        const tokens = [];
        let lastIndex = 0;
        let m;
        IDENTIFIER_REGEX.lastIndex = 0;
        while ((m = IDENTIFIER_REGEX.exec(str)) !== null) {
            if (m.index > lastIndex) {
                tokens.push({type: 'literal', value: str.substring(lastIndex, m.index)});
            }
            tokens.push({type: 'identifier', value: m[0]});
            lastIndex = m.index + m[0].length;
        }
        if (lastIndex < str.length) {
            tokens.push({type: 'literal', value: str.substring(lastIndex)});
        }
        return tokens;
    }

    _serializeDom() {
        const el = this._ref.current;
        if (!el) return '';
        const parts = [];
        const walk = (node) => {
            if (node.nodeType === Node.TEXT_NODE) {
                parts.push(node.textContent.replace(/\u00A0/g, ' '));  // nbsp → space
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                if (node.classList && node.classList.contains('formula-chip')) {
                    parts.push(node.getAttribute('data-identifier') || '');
                } else if (node.tagName === 'BR') {
                    parts.push(' ');
                } else {
                    node.childNodes.forEach(walk);
                }
            }
        };
        el.childNodes.forEach(walk);
        return parts.join('');
    }

    handleInput() {
        this._emitChange();
    }

    handlePaste(e) {
        // Forzar paste como texto plano (evita que el navegador pegue HTML/spans ajenos).
        e.preventDefault();
        const text = (e.clipboardData || window.clipboardData).getData('text');
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
            const range = sel.getRangeAt(0);
            range.deleteContents();
            range.insertNode(document.createTextNode(text));
            range.collapse(false);
        }
        this._emitChange();
    }

    _emitChange() {
        const formula = this._serializeDom();
        if (this.props.onChange) this.props.onChange(formula);
    }

    render() {
        const {placeholder} = this.props;
        return (
            <div
                ref={this._ref}
                className="chip-formula-input"
                contentEditable={true}
                spellCheck={false}
                onInput={this.handleInput}
                onPaste={this.handlePaste}
                data-placeholder={placeholder || ''}
            />
        );
    }
}

export default ChipFormulaInput;
